<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom — Directiva</p>
    <h1 class="doc__title">Tooltip</h1>
    <p class="doc__lead">
      Directiva <code>[uiTooltip]</code> que puede aplicarse a <strong>cualquier elemento</strong>
      del sistema: botones, inputs, íconos, spans, etc. Se monta en <code>document.body</code>
      con <code>position: fixed</code> para nunca ser recortado por <code>overflow: hidden</code>.
      Aparece con un retraso de <code>200ms</code> y desaparece con <code>100ms</code>.
    </p>
  </header>

  <!-- ── Posiciones ─────────────────────────────── -->
  <h2 class="doc-section-title">Posiciones</h2>

  <div class="doc-grid">
    <app-example-section title="top (default)" description="Aparece sobre el elemento.">
      <ui-button
        label="Hover aquí"
        [uiTooltip]="'Tooltip arriba'"
        uiTooltipPosition="top"
      />
    </app-example-section>

    <app-example-section title="bottom" description="Aparece bajo el elemento.">
      <ui-button
        label="Hover aquí"
        variant="secondary"
        [uiTooltip]="'Tooltip abajo'"
        uiTooltipPosition="bottom"
      />
    </app-example-section>

    <app-example-section title="left" description="Aparece a la izquierda.">
      <ui-button
        label="Hover aquí"
        variant="ghost"
        [uiTooltip]="'Tooltip izquierda'"
        uiTooltipPosition="left"
      />
    </app-example-section>

    <app-example-section title="right" description="Aparece a la derecha.">
      <ui-button
        label="Hover aquí"
        variant="ghost"
        [uiTooltip]="'Tooltip derecha'"
        uiTooltipPosition="right"
      />
    </app-example-section>
  </div>

  <!-- ── En distintos elementos ────────────────── -->
  <h2 class="doc-section-title">En distintos elementos</h2>

  <div class="doc-grid">
    <app-example-section
      title="En un ícono SVG"
      description="Aplica directamente sobre el elemento SVG o su wrapper."
    >
      <span
        class="doc-icon-wrapper"
        [uiTooltip]="'Información adicional'"
        uiTooltipPosition="top"
        tabindex="0"
        aria-label="Información"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </span>
    </app-example-section>

    <app-example-section
      title="En un input"
      description="Tooltip de ayuda sobre el campo."
    >
      <ui-input
        label="RFC"
        placeholder="XAXX010101000"
        [uiTooltip]="'El RFC debe tener 12 o 13 caracteres.'"
        uiTooltipPosition="top"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="En texto o badge"
      description="Cualquier elemento inline."
    >
      <p class="doc-inline-text">
        Estado:
        <span
          class="doc-badge"
          [uiTooltip]="'Este registro fue validado el 21/03/2026'"
          uiTooltipPosition="right"
          tabindex="0"
        >
          Activo
        </span>
      </p>
    </app-example-section>

    <app-example-section
      title="Deshabilitado"
      description="uiTooltipDisabled=true suprime el tooltip."
    >
      <ui-button
        label="Sin tooltip"
        variant="secondary"
        [uiTooltip]="'Nunca aparece'"
        [uiTooltipDisabled]="true"
      />
    </app-example-section>
  </div>

  <!-- ── Texto largo ────────────────────────────── -->
  <h2 class="doc-section-title">Contenido largo</h2>

  <div class="doc-grid">
    <app-example-section
      title="Texto multilínea"
      description="El tooltip tiene max-width: 16rem y rompe línea."
    >
      <ui-button
        label="Ver detalle"
        [uiTooltip]="'Este campo acepta valores entre 0 y 999,999.99. Usa punto o coma como separador decimal.'"
        uiTooltipPosition="bottom"
      />
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">En un botón (posición por defecto: top):</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-button
  label="Eliminar"
  [uiTooltip]="'Esta acción no se puede deshacer'"
/&gt;</code></pre>

    <p class="doc-block__p">En un ícono SVG (accesible con tabindex):</p>
    <pre class="doc-block__pre" role="code"><code>&lt;span
  [uiTooltip]="'Formato esperado: DD/MM/YYYY'"
  uiTooltipPosition="right"
  tabindex="0"
  aria-label="Ayuda de formato"
&gt;
  &lt;svg ...&gt;&lt;/svg&gt;
&lt;/span&gt;</code></pre>

    <p class="doc-block__p">Con valor dinámico:</p>
    <pre class="doc-block__pre" role="code"><code>tooltipText = computed(() =>
  &#96;Última modificación: &#36;&#123;this.lastUpdate()&#125;&#96;
);

&lt;ui-button
  label="Guardar"
  [uiTooltip]="tooltipText()"
  uiTooltipPosition="top"
/&gt;</code></pre>

    <p class="doc-block__p">Deshabilitar condicionalmente:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-button
  label="Enviar"
  [uiTooltip]="'El formulario tiene errores'"
  [uiTooltipDisabled]="form.valid"
/&gt;</code></pre>
  </section>

  <!-- ── API ───────────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">API — Inputs de la directiva</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Input</th><th>Tipo</th><th>Default</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>uiTooltip</code></td><td><code>string</code></td><td><code>''</code></td><td>Texto del tooltip. Si está vacío no aparece.</td></tr>
        <tr><td><code>uiTooltipPosition</code></td><td><code>'top' | 'bottom' | 'left' | 'right'</code></td><td><code>'top'</code></td><td>Posición relativa al elemento host.</td></tr>
        <tr><td><code>uiTooltipDisabled</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Suprime el tooltip completamente.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ──────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>En elementos que no son interactivos (spans, SVGs), añade <code>tabindex="0"</code> y <code>aria-label</code> para que el tooltip sea accesible con teclado.</li>
      <li>Mantén el texto breve: el tooltip es un complemento, no un reemplazo del label.</li>
      <li>Usa <code>[uiTooltipDisabled]="condition"</code> para mostrar el tooltip solo cuando sea relevante (p. ej., botón deshabilitado).</li>
      <li>Evita poner información crítica solo en tooltips; son invisibles en mobile táctil.</li>
      <li>El tooltip se oculta automáticamente al presionar <code>Escape</code>.</li>
    </ul>
  </section>
</article>
